import React from 'react';

interface QRPreviewProps {
  previewImageUrl?: string;
  serialNumber?: string;
  orientation?: 'portrait' | 'landscape';
  isLoading?: boolean;
  isTiffGenerating?: boolean;
  isGoogleDriveUploading?: boolean;
  googleDriveUploadCompleted?: boolean;
  onDownloadPng?: () => void;
  onUploadToGoogleDrive?: () => void;
}

export const QRPreview: React.FC<QRPreviewProps> = ({
  previewImageUrl,
  serialNumber,
  orientation,
  isLoading = false,
  isTiffGenerating = false,
  isGoogleDriveUploading = false,
  googleDriveUploadCompleted = false,
  onDownloadPng,
  onUploadToGoogleDrive,
}) => {
  const handlePngDownload = async () => {
    if (previewImageUrl && serialNumber && orientation && onDownloadPng) {
      onDownloadPng();
    }
  };


  const handleGoogleDriveUpload = async () => {
    if (previewImageUrl && serialNumber && orientation && onUploadToGoogleDrive) {
      onUploadToGoogleDrive();
    }
  };

  return (
    <div className="tab-container">
      {/* Use same tab navigation structure as TabContainer */}
      <div className="tab-navigation">
        <div className="tab-button active">
          <span className="tab-icon">📸</span>
          <span className="tab-label">Manufacturing Preview</span>
        </div>
      </div>
      
      {/* Use same tab content structure as TabContainer */}
      <div className="tab-content">
        <div style={containerStyle}>
          <div className="preview-area" style={previewAreaStyle}>
          {isLoading ? (
            <div style={loadingStyle}>
              <div style={spinnerStyle}></div>
              <p>Generating composite image...</p>
            </div>
          ) : previewImageUrl ? (
            <>
              <div style={imageContainerStyle}>
                <img
                  src={previewImageUrl}
                  alt={`${orientation} QR Code Preview`}
                  style={{
                    ...imageStyle,
                    ...(orientation === 'portrait' ? portraitImageStyle : landscapeImageStyle)
                  }}
                />
                <div style={imageInfoStyle}>
                  <p style={infoTextStyle}>
                    <strong>Serial:</strong> {serialNumber}
                  </p>
                  <p style={infoTextStyle}>
                    <strong>Orientation:</strong> {orientation ? orientation.charAt(0).toUpperCase() + orientation.slice(1) : 'Not specified'}
                  </p>
                  <p style={infoTextStyle}>
                    <strong>Dimensions:</strong> {orientation === 'portrait' ? '1200 × 2100' : '2100 × 1200'} pixels
                  </p>
                </div>
              </div>
              
              {serialNumber && orientation && (
                <div style={actionsStyle}>
                  <div style={compactButtonRowStyle}>
                    <button
                      onClick={handlePngDownload}
                      style={compactButtonStyle}
                    >
                      📥 Download PNG
                    </button>
                    <button
                      onClick={handleGoogleDriveUpload}
                      disabled={isTiffGenerating || isGoogleDriveUploading || googleDriveUploadCompleted}
                      style={{
                        ...compactButtonStyle,
                        ...(googleDriveUploadCompleted ? compactButtonCompletedStyle : {}),
                        ...(isTiffGenerating || isGoogleDriveUploading ? compactButtonDisabledStyle : {}),
                      }}
                      title={googleDriveUploadCompleted ? 'Already uploaded to Google Drive' : 'Upload TIFF to Google Drive'}
                    >
                      {googleDriveUploadCompleted ? (
                        '✅ Uploaded'
                      ) : (
                        '🚀 Generate & Upload'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Status Popup */}
              {(isTiffGenerating || isGoogleDriveUploading) && (
                <div style={popupOverlayStyle}>
                  <div style={popupStyle}>
                    <div style={popupIconStyle}>
                      {isTiffGenerating ? '📄' : '📁'}
                    </div>
                    <h3 style={popupTitleStyle}>
                      {isTiffGenerating ? 'Generating high-resolution TIFF file...' : 'Uploading to Google Drive...'}
                    </h3>
                    <p style={popupSubtextStyle}>
                      {isTiffGenerating
                        ? 'This may take 10-15 seconds to process'
                        : 'Authenticating and uploading file'
                      }
                    </p>
                    <div style={popupSpinnerStyle}></div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={placeholderStyle}>
              <div style={placeholderIconStyle}>🖼️</div>
              <p style={placeholderTextStyle}>
                Click "Create Portrait" or "Create Landscape" to generate a preview
              </p>
              <p style={placeholderSubtextStyle}>
                The composite image will show your QR code positioned on the manufacturing template
              </p>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

// Styles

const containerStyle: React.CSSProperties = {
  padding: '12px',
  background: '#ffffff',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const previewAreaStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  background: '#f8f9fa',
  border: '2px dashed #d0d7de',
  borderRadius: '8px',
  flex: '1',
  position: 'relative',
  overflow: 'auto',
  minHeight: '600px',
  maxHeight: '800px',
  padding: '20px 10px',
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '20px',
  color: '#666',
};

const spinnerStyle: React.CSSProperties = {
  width: '40px',
  height: '40px',
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #6600ff',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

const imageContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '15px',
  maxWidth: '100%',
  width: '100%',
};

const imageStyle: React.CSSProperties = {
  maxWidth: '100%',
  maxHeight: 'none',
  objectFit: 'contain',
  border: '1px solid #ddd',
  borderRadius: '4px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
};

const portraitImageStyle: React.CSSProperties = {
  maxWidth: '280px',
  width: 'auto',
  height: 'auto',
};

const landscapeImageStyle: React.CSSProperties = {
  maxWidth: '90%',
  width: 'auto',
  maxHeight: '400px',
};

const imageInfoStyle: React.CSSProperties = {
  background: '#fff',
  padding: '10px 15px',
  borderRadius: '6px',
  border: '1px solid #e0e0e0',
  fontSize: '14px',
  textAlign: 'center',
};

const infoTextStyle: React.CSSProperties = {
  margin: '4px 0',
  color: '#333',
};

const placeholderStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '15px',
  color: '#666',
  textAlign: 'center',
  padding: '40px',
  flex: '1',
  justifyContent: 'center',
};

const placeholderIconStyle: React.CSSProperties = {
  fontSize: '64px',
  opacity: 0.5,
};

const placeholderTextStyle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: '500',
  margin: '0',
  color: '#444',
};

const placeholderSubtextStyle: React.CSSProperties = {
  fontSize: '14px',
  margin: '0',
  color: '#666',
  maxWidth: '300px',
  lineHeight: '1.4',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
  marginTop: '15px',
  paddingTop: '15px',
  borderTop: '1px solid #e0e0e0',
};


// Removed unused button styles - now using compact styles

// Compact button styles
const compactButtonRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '10px',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  maxWidth: '100%',
};

const compactButtonStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #6600ff, #4d00c0)',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: '500',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  transition: 'all 0.2s',
  boxShadow: '0 2px 4px rgba(102, 0, 255, 0.2)',
  minWidth: '140px',
  justifyContent: 'center',
};

const compactButtonCompletedStyle: React.CSSProperties = {
  background: '#28a745',
  cursor: 'not-allowed',
};

const compactButtonDisabledStyle: React.CSSProperties = {
  background: '#999',
  cursor: 'not-allowed',
  opacity: 0.7,
};

// Popup styles
const popupOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const popupStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '30px',
  maxWidth: '400px',
  width: '90%',
  textAlign: 'center',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
  position: 'relative',
};

const popupIconStyle: React.CSSProperties = {
  fontSize: '48px',
  marginBottom: '15px',
};

const popupTitleStyle: React.CSSProperties = {
  margin: '0 0 10px 0',
  fontSize: '18px',
  fontWeight: '600',
  color: '#333',
};

const popupSubtextStyle: React.CSSProperties = {
  margin: '0 0 20px 0',
  fontSize: '14px',
  color: '#666',
  lineHeight: '1.4',
};

const popupSpinnerStyle: React.CSSProperties = {
  width: '32px',
  height: '32px',
  border: '3px solid #f3f3f3',
  borderTop: '3px solid #6600ff',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  margin: '0 auto',
};

// Add CSS for spinner animation
const spinnerCSS = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject spinner CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = spinnerCSS;
  document.head.appendChild(style);
}